import React, { useEffect, useState, useRef } from "react";
import api from "../../lib/api";
import { Plus, Pencil, Trash2, Loader2, Search, Upload } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useToast } from "../../hooks/use-toast";

export default function ResourceManager({ title, endpoint, fields, listColumns, defaults }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaults);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const fetchItems = () => {
    setLoading(true);
    api.get(`/${endpoint}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const startNew = () => {
    setForm(defaults);
    setEditing(null);
    setOpen(true);
  };

  const startEdit = (item) => {
    setForm({ ...defaults, ...item });
    setEditing(item.id);
    setOpen(true);
  };

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      fields.forEach((f) => {
        let v = form[f.name];
        if (f.type === "number") v = Number(v) || 0;
        if (f.type === "array") {
          v = typeof v === "string"
            ? v.split("\n").map((x) => x.trim()).filter(Boolean)
            : v || [];
        }
        if (f.type === "boolean") v = !!v;
        payload[f.name] = v;
      });
      if (editing) {
        await api.put(`/${endpoint}/${editing}`, payload);
        toast({ title: "Updated", description: `${title} item updated.` });
      } else {
        await api.post(`/${endpoint}`, payload);
        toast({ title: "Created", description: `${title} item created.` });
      }
      setOpen(false);
      fetchItems();
    } catch (err) {
      toast({
        title: "Error",
        description: err?.response?.data?.detail || "Save failed",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/${endpoint}/${id}`);
      toast({ title: "Deleted" });
      fetchItems();
    } catch (err) {
      toast({ title: "Error", description: "Delete failed", variant: "destructive" });
    }
  };

  const filtered = items.filter((it) => {
    if (!query) return true;
    return JSON.stringify(it).toLowerCase().includes(query.toLowerCase());
  });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#0B2A6B]">{title}</h2>
          <p className="text-sm text-slate-500">Manage {title.toLowerCase()} content</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input className="pl-9 w-60" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search..." />
          </div>
          <Button onClick={startNew} className="bg-yellow-400 hover:bg-yellow-500 text-[#0B2A6B] font-bold">
            <Plus className="h-4 w-4 mr-1" /> New
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {listColumns.map((c) => (
                  <th key={c.key} className="text-left px-5 py-3 font-semibold text-slate-700">
                    {c.label}
                  </th>
                ))}
                <th className="text-right px-5 py-3 font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={listColumns.length + 1} className="text-center py-12">
                    <Loader2 className="h-5 w-5 animate-spin inline" />
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={listColumns.length + 1} className="text-center py-12 text-slate-400">
                    No items
                  </td>
                </tr>
              ) : (
                filtered.map((it) => (
                  <tr key={it.id} className="border-b border-slate-100 hover:bg-yellow-50/40 transition">
                    {listColumns.map((c) => (
                      <td key={c.key} className="px-5 py-3 align-top max-w-xs">
                        {c.render
                          ? c.render(it[c.key], it)
                          : typeof it[c.key] === "boolean"
                          ? it[c.key] ? "Yes" : "No"
                          : (it[c.key] ?? "-")}
                      </td>
                    ))}
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => startEdit(it)}
                        className="text-blue-600 hover:text-blue-800 mr-3"
                        aria-label="Edit"
                      >
                        <Pencil className="h-4 w-4 inline" />
                      </button>
                      <button
                        onClick={() => remove(it.id)}
                        className="text-red-500 hover:text-red-700"
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${title}` : `New ${title}`}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 pt-2">
            <div className="grid sm:grid-cols-2 gap-4">
              {fields.map((f) => (
                <div key={f.name} className={f.col === "full" ? "sm:col-span-2" : ""}>
                  <Label className="text-sm font-semibold text-slate-700 mb-1.5 inline-block">
                    {f.label}{f.required ? " *" : ""}
                  </Label>
                  {f.type === "textarea" ? (
                    <Textarea
                      rows={f.rows || 3}
                      value={form[f.name] || ""}
                      onChange={(e) => onChange(f.name, e.target.value)}
                      placeholder={f.placeholder}
                      required={f.required}
                    />
                  ) : f.type === "array" ? (
                    <Textarea
                      rows={3}
                      value={Array.isArray(form[f.name]) ? form[f.name].join("\n") : (form[f.name] || "")}
                      onChange={(e) => onChange(f.name, e.target.value)}
                      placeholder={f.placeholder || "One item per line"}
                    />
                  ) : f.type === "select" ? (
                    <Select value={form[f.name] || ""} onValueChange={(v) => onChange(f.name, v)}>
                      <SelectTrigger>
                        <SelectValue placeholder={f.placeholder || "Select..."} />
                      </SelectTrigger>
                      <SelectContent>
                        {f.options.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : f.type === "boolean" ? (
                    <div className="flex items-center gap-3 h-10">
                      <Switch checked={!!form[f.name]} onCheckedChange={(v) => onChange(f.name, v)} />
                      <span className="text-sm text-slate-600">{form[f.name] ? "Active / Yes" : "Inactive / No"}</span>
                    </div>
                  ) : f.type === "number" ? (
                    <Input
                      type="number"
                      value={form[f.name] ?? 0}
                      onChange={(e) => onChange(f.name, e.target.value)}
                      placeholder={f.placeholder}
                    />
                  ) : f.type === "image" ? (
                    <ImageField
                      value={form[f.name] || ""}
                      onChange={(v) => onChange(f.name, v)}
                      placeholder={f.placeholder || "Paste URL or upload"}
                      required={f.required}
                    />
                  ) : (
                    <Input
                      type={f.type || "text"}
                      value={form[f.name] || ""}
                      onChange={(e) => onChange(f.name, e.target.value)}
                      placeholder={f.placeholder}
                      required={f.required}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-3">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-yellow-400 hover:bg-yellow-500 text-[#0B2A6B] font-bold">
                {saving ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
                ) : (editing ? "Update" : "Create")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ImageField({ value, onChange, placeholder, required }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const backendBase = process.env.REACT_APP_BACKEND_URL || "";

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("File too large (max 5MB)");
      return;
    }
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Return absolute URL so <img src> works directly on the public site
      const absolute = res.data.url.startsWith("http")
        ? res.data.url
        : `${backendBase}${res.data.url}`;
      onChange(absolute);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  // Build preview URL — relative /api/files/... paths are still rendered via backend
  const previewUrl = value
    ? value.startsWith("http") || value.startsWith("/")
      ? value.startsWith("/api/files")
        ? `${backendBase}${value}`
        : value
      : ""
    : "";

  return (
    <div className="space-y-2" data-testid="image-field">
      <div className="flex gap-2">
        <Input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          data-testid="image-url-input"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-200 bg-yellow-50 text-[#0B2A6B] font-semibold text-sm hover:bg-yellow-100 disabled:opacity-50 whitespace-nowrap"
          data-testid="image-upload-button"
          title="Upload from your computer"
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          {uploading ? "Uploading…" : "Upload"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
          className="hidden"
          onChange={handleFile}
          data-testid="image-file-input"
        />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      {previewUrl && (
        <div className="rounded-md border border-slate-200 overflow-hidden w-28 h-28 bg-slate-50">
          <img
            src={previewUrl}
            alt="preview"
            className="w-full h-full object-cover"
            onError={(e) => (e.target.style.display = "none")}
          />
        </div>
      )}
    </div>
  );
}
